const DB_NAME = "poseref-reference-images";
const DB_VERSION = 1;
const STORE_NAME = "images";

export interface RefImageRecord {
  hash: string;
  name: string;
  blob: Blob;
  size: number;
  createdAt: number;
}

export interface RefImageMeta {
  hash: string;
  name: string;
  size: number;
  createdAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openRefImageDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "hash" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });
  return dbPromise;
}

export async function storeReferenceImage(
  hash: string,
  name: string,
  blob: Blob,
): Promise<void> {
  const db = await openRefImageDB();
  const record: RefImageRecord = {
    hash,
    name,
    blob,
    size: blob.size,
    createdAt: Date.now(),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getReferenceImage(
  hash: string,
): Promise<{ blob: Blob; name: string } | null> {
  const db = await openRefImageDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(hash);
    req.onsuccess = () => {
      const record = req.result as RefImageRecord | undefined;
      resolve(record ? { blob: record.blob, name: record.name } : null);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function deleteReferenceImage(hash: string): Promise<void> {
  const db = await openRefImageDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(hash);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllReferenceImages(): Promise<RefImageMeta[]> {
  const db = await openRefImageDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => {
      const records = req.result as RefImageRecord[];
      resolve(
        records.map(({ hash, name, size, createdAt }) => ({
          hash,
          name,
          size,
          createdAt,
        })),
      );
    };
    req.onerror = () => reject(req.error);
  });
}

export async function getReferenceImageUrl(
  hash: string,
): Promise<string | null> {
  const record = await getReferenceImage(hash);
  if (!record) return null;
  return URL.createObjectURL(record.blob);
}
